import { useCreateProject } from './CreateProjectContext';
import { useForm, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import MarkdownEditor from '../../../../components/MarkdownEditor';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  projectDetailsSchema,
  type ProjectDetailsForm,
} from '@shared/schemas/project';

export default function StepOne() {
  const { formState, setFormState, resetForm } = useCreateProject();
  const navigate = useNavigate();
  const [calOpen, setCalOpen] = useState(false);

  const form = useForm<ProjectDetailsForm>({
    resolver: zodResolver(projectDetailsSchema as never),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: {
      projectName: formState.projectName,
      brief: formState.brief,
      dueDate: formState.dueDate,
    },
  });

  const watchedValues = useWatch({ control: form.control });

  useEffect(() => {
    setFormState((prev) => ({ ...prev, ...watchedValues }));
  }, [watchedValues, setFormState]);

  function onNext(values: ProjectDetailsForm) {
    setFormState((prev) => ({
      ...prev,
      ...values,
    }));
    navigate('deliverables');
  }

  function handleCancel() {
    resetForm();
    navigate('/client');
  }

  const tomorrow = new Date();
  tomorrow.setHours(0, 0, 0, 0);
  tomorrow.setDate(tomorrow.getDate() + 1);

  return (
    <>
      <div className="flex flex-col gap-12 h-full overflow-hidden">
        <Form {...form}>
          <form
            onSubmit={(e) => {
              console.log('SUBMIT FIRED', e.target, e.nativeEvent);
              console.trace();
              form.handleSubmit(onNext)(e);
            }}
            className="flex flex-1 flex-col min-h-0 overflow-y-auto space-y-6 max-w-300"
          >
            <h1 className="text-2xl font-bold">Project Details</h1>

            <FormField
              control={form.control}
              name="projectName"
              render={({ field }) => (
                <FormItem className="space-y-1">
                  <FormLabel>Project Name</FormLabel>
                  <FormControl>
                    <Input {...field} autoComplete="off" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="brief"
              render={({ field }) => (
                <FormItem className="flex flex-col flex-1 min-h-0 space-y-1">
                  <FormLabel>Brief</FormLabel>
                  <FormControl>
                    <MarkdownEditor
                      value={field.value ?? ''}
                      onChange={field.onChange}
                      onBlur={field.onBlur}
                      placeholder="Describe your project.."
                      className="flex flex-1 min-h-0"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="dueDate"
              render={({ field }) => {
                return (
                  <FormItem className="max-w-48 space-y-1">
                    <FormLabel>Deliver by</FormLabel>
                    <FormControl>
                      <Popover open={calOpen} onOpenChange={setCalOpen}>
                        <PopoverTrigger asChild>
                          <Button
                            variant={'outline'}
                            data-empty={!field.value}
                            className="w-70 justify-start text-left font-normal data-[empty=true]:text-muted-foreground"
                          >
                            <CalendarIcon className="mr-2 h-4 w-4" />
                            {field.value
                              ? new Date(field.value).toLocaleDateString()
                              : 'Select date'}
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-auto p-0">
                          <Calendar
                            mode="single"
                            selected={
                              field.value ? new Date(field.value) : undefined
                            }
                            onSelect={(date) => {
                              field.onChange(date?.toISOString());
                              setCalOpen(false);
                            }}
                            fixedWeeks
                            disabled={{ before: tomorrow }}
                          />
                        </PopoverContent>
                      </Popover>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
            <div className="flex justify-between pb-3">
              <Button
                className="cursor-pointer"
                type="button"
                onClick={handleCancel}
                variant={'ghost'}
              >
                Cancel
              </Button>
              <Button className="cursor-pointer" type="submit">
                Next
              </Button>
            </div>
          </form>
        </Form>
      </div>
    </>
  );
}
